import React, { useState, useEffect, useMemo, useRef } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { ChevronRight } from 'lucide-react'
import {
  getCheckins,
  getProjects,
  getWeekDigests,
  Checkin,
  Project,
  WeekDigest,
} from '../api/client'
import { toLocalDateStr, toShortLabel, parseLocalDateStr } from '../utils/dates'

interface ProjectActivityAreaChartProps {
  className?: string
}

// Ridge ramps, front ridge (most active project) → back (least active). Both
// pass adjacent-pair CVD and normal-vision separation (worst ΔE 15.1 day,
// 15.7 night); the haze look deliberately ignores the usual lightness/chroma
// rules, with identity carried by the legend and tooltip rather than hue
// alone. The scene blends between the two ramps with the sky as night falls.
const DAY_RIDGE_COLORS = ['#3F2C68', '#7D4F90', '#C3789A', '#F0A68D', '#FFE6BF']
const NIGHT_RIDGE_COLORS = ['#221838', '#5E3878', '#A06384', '#D2949B', '#F6D0B4']
// Rim light along each ridge's top edge — sunlit by day, moonlit by night.
const DAY_RIM_COLORS = ['#8E77C2', '#B58BC4', '#E8A8BC', '#FFCDA8', '#FFF3D9']
const NIGHT_RIM_COLORS = ['#7C639E', '#9B7BB0', '#C795A6', '#EBB9AE', '#FFE3C4']
const MAX_PROJECTS = DAY_RIDGE_COLORS.length
// How far the first and last date labels sit in from the card's edges.
const TICK_EDGE_INSET = 16

export interface SkyScheme {
  sky: string[] // five gradient stops, zenith → horizon
  sun: string // disc color (the moon's, at night)
  ground: string
  ridgeT: number // 0 = day ridge ramp, 1 = night ramp
  stars: number // 0..1 star visibility
}

// Keyframes over the viewer's local hour; schemeForHour interpolates between
// neighbors, wrapping past midnight. Stylized, not solar-accurate.
const SCHEME_STOPS: [number, SkyScheme][] = [
  [0, { sky: ['#12102A', '#1B1840', '#2A2354', '#3A2E62', '#4A3A6E'], sun: '#E9E7F4', ground: '#0E0A1E', ridgeT: 1, stars: 0.9 }],
  [4.5, { sky: ['#1A1838', '#2A2352', '#463672', '#6A4A80', '#8A5C7E'], sun: '#EFE3D8', ground: '#140F28', ridgeT: 1, stars: 0.75 }],
  [6.5, { sky: ['#4A4E96', '#7A69AC', '#C288A6', '#F0A986', '#FFD9A8'], sun: '#FFE9C4', ground: '#241B44', ridgeT: 0.5, stars: 0 }],
  [9, { sky: ['#6D93D8', '#8FA9DE', '#C0B4D8', '#F0CBA0', '#FFEECB'], sun: '#FFF6DE', ground: '#333058', ridgeT: 0, stars: 0 }],
  [13, { sky: ['#5E9BE0', '#82B2E8', '#B8CCE8', '#E8DEC0', '#FFF4D8'], sun: '#FFFBE8', ground: '#3A3A64', ridgeT: 0, stars: 0 }],
  [17.5, { sky: ['#6C61B8', '#9B6FB4', '#DE8393', '#FFA96E', '#FFE3B0'], sun: '#FFF3D6', ground: '#332353', ridgeT: 0, stars: 0 }],
  [20, { sky: ['#322B54', '#5C4276', '#A05C74', '#E08558', '#FFD9A4'], sun: '#FFF3D6', ground: '#1B1230', ridgeT: 0.7, stars: 0.25 }],
  [21.5, { sky: ['#12102A', '#1B1840', '#2A2354', '#3A2E62', '#4A3A6E'], sun: '#E9E7F4', ground: '#0E0A1E', ridgeT: 1, stars: 0.9 }],
]

function lerpHex(a: string, b: string, t: number): string {
  if (a === b || t <= 0) return a
  if (t >= 1) return b
  const pa = parseInt(a.slice(1), 16)
  const pb = parseInt(b.slice(1), 16)
  const ch = (shift: number) => {
    const ca = (pa >> shift) & 0xff
    return Math.round(ca + (((pb >> shift) & 0xff) - ca) * t)
  }
  return `#${((ch(16) << 16) | (ch(8) << 8) | ch(0)).toString(16).padStart(6, '0')}`
}

export function schemeForHour(hour: number): SkyScheme {
  const h = ((hour % 24) + 24) % 24
  let i = SCHEME_STOPS.length - 1
  for (let k = 0; k < SCHEME_STOPS.length; k++)
    if (SCHEME_STOPS[k][0] <= h) i = k
  const [h0, a] = SCHEME_STOPS[i]
  const [h1raw, b] = SCHEME_STOPS[(i + 1) % SCHEME_STOPS.length]
  const h1 = h1raw > h0 ? h1raw : h1raw + 24
  const t = (h - h0) / (h1 - h0)
  return {
    sky: a.sky.map((c, k) => lerpHex(c, b.sky[k], t)),
    sun: lerpHex(a.sun, b.sun, t),
    ground: lerpHex(a.ground, b.ground, t),
    ridgeT: a.ridgeT + (b.ridgeT - a.ridgeT) * t,
    stars: a.stars + (b.stars - a.stars) * t,
  }
}

// Fixed stylized sunrise/sunset — the arc needs no geolocation to feel right.
const SUNRISE = 6.5
const SUNSET = 20

export function celestialForHour(hour: number): { body: 'sun' | 'moon'; progress: number } {
  const h = ((hour % 24) + 24) % 24
  if (h >= SUNRISE && h <= SUNSET)
    return { body: 'sun', progress: (h - SUNRISE) / (SUNSET - SUNRISE) }
  const since = h > SUNSET ? h - SUNSET : h + 24 - SUNSET
  return { body: 'moon', progress: since / (24 - (SUNSET - SUNRISE)) }
}

// A deterministic scatter of stars in the upper sky (fractions of W/H).
const STARS = Array.from({ length: 42 }, (_, i) => {
  const fract = (v: number) => v - Math.floor(v)
  return {
    x: fract(Math.sin(i * 127.1) * 43758.5453),
    y: fract(Math.sin(i * 311.7) * 12543.853) * 0.55,
    r: 0.5 + ((i * 7) % 10) / 12,
  }
})

interface RidgeSeries {
  project: Project
  values: number[] // one count per day of the month
  total: number
}

export interface MonthActivity {
  dates: string[] // YYYY-MM-DD, one per day of the displayed month
  series: RidgeSeries[] // front → back, i.e. most → least active
  monthLabel: string
}

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
                     'July', 'August', 'September', 'October', 'November', 'December']

function monthLabelFor(monthOffset: number): string {
  const today = new Date()
  const target = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1)
  return target.getFullYear() === today.getFullYear()
    ? MONTH_NAMES[target.getMonth()]
    : `${MONTH_NAMES[target.getMonth()]} ${target.getFullYear()}`
}

/**
 * Bucket checkins and tasks into per-project daily counts for the month at
 * `monthOffset` (0 = current). Kept pure so the date-alignment tests can pin
 * it directly: every date is handled as a local-calendar string, never
 * round-tripped through `Date` (see utils/dates.ts for the failure modes).
 */
export function buildMonthActivity(
  checkins: Checkin[],
  tasks: any[],
  projects: Project[],
  monthOffset: number
): MonthActivity {
  const today = new Date()
  const targetDate = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1)
  const monthLabel = monthLabelFor(monthOffset)

  const daysInMonth = new Date(targetDate.getFullYear(), targetDate.getMonth() + 1, 0).getDate()
  const dates: string[] = []
  const dayIndex = new Map<string, number>()
  for (let day = 1; day <= daysInMonth; day++) {
    const dateStr = toLocalDateStr(new Date(targetDate.getFullYear(), targetDate.getMonth(), day))
    dayIndex.set(dateStr, dates.length)
    dates.push(dateStr)
  }

  const perProject = new Map<number, number[]>()
  const countOn = (projectId: number, dateStr: string) => {
    const idx = dayIndex.get(dateStr)
    if (idx === undefined) return
    let values = perProject.get(projectId)
    if (!values) {
      values = new Array(daysInMonth).fill(0)
      perProject.set(projectId, values)
    }
    values[idx] += 1
  }

  checkins.forEach(checkin => {
    const dateStr = checkin.applies_to.split('T')[0]
    checkin.checkin_projects?.forEach(cp => countOn(cp.project_id, dateStr))
  })
  tasks.forEach(task => {
    const dateStr = task.created_at.split('T')[0]
    task.project_details?.forEach((p: any) => countOn(p.id, dateStr))
  })

  const series: RidgeSeries[] = projects
    .filter(p => perProject.has(p.id))
    .map(p => {
      const values = perProject.get(p.id)!
      return { project: p, values, total: values.reduce((a, b) => a + b, 0) }
    })
    .sort((a, b) => b.total - a.total)
    .slice(0, MAX_PROJECTS)

  return { dates, series, monthLabel }
}

/**
 * Monotone cubic interpolation (Fritsch–Carlson) — the same shape recharts'
 * `type="monotone"` draws, so ridges roll through the data without dipping
 * below a run of zero days.
 */
function monotoneTopEdge(pts: [number, number][]): string {
  const n = pts.length
  if (n === 0) return ''
  if (n === 1) return `M ${pts[0][0].toFixed(1)} ${pts[0][1].toFixed(1)}`
  const dx: number[] = [], slope: number[] = [], m: number[] = []
  for (let i = 0; i < n - 1; i++) {
    dx[i] = pts[i + 1][0] - pts[i][0]
    slope[i] = (pts[i + 1][1] - pts[i][1]) / dx[i]
  }
  m[0] = slope[0]
  m[n - 1] = slope[n - 2]
  for (let i = 1; i < n - 1; i++)
    m[i] = slope[i - 1] * slope[i] <= 0 ? 0 : (slope[i - 1] + slope[i]) / 2
  for (let i = 0; i < n - 1; i++) {
    if (slope[i] === 0) { m[i] = 0; m[i + 1] = 0; continue }
    const a = m[i] / slope[i], b = m[i + 1] / slope[i], h = Math.hypot(a, b)
    if (h > 3) { m[i] = 3 / h * a * slope[i]; m[i + 1] = 3 / h * b * slope[i] }
  }
  let d = `M ${pts[0][0].toFixed(1)} ${pts[0][1].toFixed(1)}`
  for (let i = 0; i < n - 1; i++) {
    const c1x = pts[i][0] + dx[i] / 3, c1y = pts[i][1] + m[i] * dx[i] / 3
    const c2x = pts[i + 1][0] - dx[i] / 3, c2y = pts[i + 1][1] - m[i + 1] * dx[i] / 3
    d += ` C ${c1x.toFixed(1)} ${c1y.toFixed(1)}, ${c2x.toFixed(1)} ${c2y.toFixed(1)}, ${pts[i + 1][0].toFixed(1)} ${pts[i + 1][1].toFixed(1)}`
  }
  return d
}

// Display-only smoothing: rounds an isolated active day from a needle into a
// hill. The tooltip always reports the raw counts.
const smoothValues = (a: number[]) =>
  a.map((v, i) => 0.2 * (a[i - 1] ?? 0) + 0.6 * v + 0.2 * (a[i + 1] ?? 0))

interface HoverState {
  dayIdx: number
  x: number // card-relative px
  y: number
}

// ---------------------------------------------------------------------------
// Week digests
//
// `period_digest` (see backend/server/summaries/builders.py) writes one prose
// digest per ISO week, and until now it existed only to be injected into parse
// prompts — there was no way to read your own. The landscape already answers
// "which days were busy"; the digest answers "what was actually going on", so
// it hangs off the same surface: click a day, get that day's WEEK, read it in
// an expandable row under the legend. Paging months walks the weeks.
//
// The row is collapsed by default so the hero keeps its presence, and the
// tooltip is left alone — it reports raw per-day counts, which is a different
// question and still the right one to answer on hover.
// ---------------------------------------------------------------------------

const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** The Monday of the ISO week containing a local `YYYY-MM-DD` day.
 *  Calendar math only — parts in, parts out, never `new Date(dateStr)`
 *  (see utils/dates.ts for what that does at a negative offset). */
export function weekStartFor(dateStr: string): string {
  const { year, month, day } = parseLocalDateStr(dateStr)
  const d = new Date(year, month - 1, day)
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7)) // Sun=0 → 6 back, Mon=1 → 0
  return toLocalDateStr(d)
}

/** The Sunday closing the week that starts on `mondayStr`. INCLUSIVE, matching
 *  the backend's `period_bounds` contract, so it is also the `period_end` a
 *  built row carries — which is why a week with no row can still be labelled. */
export function weekEndFor(mondayStr: string): string {
  const { year, month, day } = parseLocalDateStr(mondayStr)
  return toLocalDateStr(new Date(year, month - 1, day + 6))
}

/** Every ISO week the displayed month touches, ordered, keyed by its Monday.
 *  The first and last are usually partial months-wise, and that is deliberate:
 *  the digest's unit is the whole week, so the row offers the weeks the month
 *  is part of rather than clipped versions of them. */
export function weeksForMonth(dates: string[]): string[] {
  const weeks: string[] = []
  for (const dateStr of dates) {
    const start = weekStartFor(dateStr)
    if (weeks[weeks.length - 1] !== start) weeks.push(start)
  }
  return weeks
}

/** "1 – 7 Sep", "29 Sep – 5 Oct", "29 Dec 2025 – 4 Jan 2026". The month is
 *  repeated on the left only when the week straddles two, and the years appear
 *  only when it straddles two of those. */
export function weekRangeLabel(mondayStr: string, sundayStr: string): string {
  const s = parseLocalDateStr(mondayStr)
  const e = parseLocalDateStr(sundayStr)
  const crossYear = s.year !== e.year
  const part = (p: { year: number; month: number; day: number }, withMonth: boolean) =>
    `${p.day}${withMonth ? ` ${SHORT_MONTHS[p.month - 1]}` : ''}${crossYear ? ` ${p.year}` : ''}`
  return `${part(s, s.month !== e.month || crossYear)} – ${part(e, true)}`
}

/** What a week's digest looks like to a reader. Telling the three build modes
 *  apart IS the review, so each gets its own label and its own explanation of
 *  why it reads the way it does.
 *
 *  Two degradations, neither of them an error:
 *   - `missing` — no row was built for this week at all (the backfill hasn't
 *     reached it, or the user has none). Normal; says so and shows nothing.
 *   - `unlabelled` — a row built before `digest_mode` was recorded. The prose
 *     still reads fine; we just decline to guess which mode produced it. */
type DigestMode = 'prose' | 'verbatim' | 'empty' | 'unlabelled' | 'missing'

export interface DigestDisplay {
  mode: DigestMode
  label: string
  /** One line on how the week was written up — null when there's nothing to add. */
  detail: string | null
  /** The digest prose, or null when there is none to show. */
  text: string | null
}

export function describeDigest(digest: WeekDigest | undefined): DigestDisplay {
  if (!digest) {
    return {
      mode: 'missing',
      label: 'Not built',
      detail: 'No digest has been built for this week.',
      text: null,
    }
  }
  const words = digest.source_word_count
  const sourceWords = words === null ? null : `${words} word${words === 1 ? '' : 's'} of check-ins`
  const quiet: DigestDisplay = {
    mode: 'empty',
    label: 'Quiet week',
    detail: 'No check-in this week had a body, so there was nothing to digest.',
    text: null,
  }

  switch (digest.digest_mode) {
    case 'prose':
      return {
        mode: 'prose',
        label: 'Summarized',
        detail: sourceWords && `${sourceWords}, over the budget — a model wrote this.`,
        text: digest.text,
      }
    case 'verbatim':
      return {
        mode: 'verbatim',
        label: 'Verbatim',
        detail: sourceWords
          ? `${sourceWords}, under the budget — these are the check-ins, no model ran.`
          : 'Under the word budget — these are the check-ins, no model ran.',
        text: digest.text,
      }
    case 'empty':
      return quiet
    default:
      // No `digest_mode` key: a row from before the word budget landed.
      return digest.text
        ? {
            mode: 'unlabelled',
            label: 'Digest',
            detail: 'Built before the digest recorded how it was written.',
            text: digest.text,
          }
        : quiet
  }
}

const MODE_BADGE: Record<DigestMode, string> = {
  prose: 'bg-amber-100 text-amber-800',
  verbatim: 'bg-emerald-100 text-emerald-800',
  empty: 'bg-gray-200 text-gray-500',
  unlabelled: 'bg-gray-200 text-gray-600',
  missing: 'bg-gray-200 text-gray-500',
}

// The two documented springs: buttons/toggling icons, and surfaces.
const BUTTON_SPRING = { type: 'spring', stiffness: 400, damping: 20 } as const
const SURFACE_SPRING = { type: 'spring', stiffness: 500, damping: 30 } as const

const ModeBadge: React.FC<{ display: DigestDisplay }> = ({ display }) => (
  <span
    data-testid="week-digest-mode"
    data-mode={display.mode}
    className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${MODE_BADGE[display.mode]}`}
  >
    {display.label}
  </span>
)

interface WeekDigestRowProps {
  /** The Mondays the displayed month touches, in order. */
  weeks: string[]
  /** Monday → the built digest, for the weeks that have one. */
  digests: Record<string, WeekDigest>
  selected: string | null
  onSelect: (monday: string) => void
  open: boolean
  onToggle: () => void
}

/** The expandable digest row, directly under the legend band — the bottom edge
 *  of the full-bleed hero. Its inner paddings reproduce the legend's, so the
 *  header lines up with the first legend swatch above it. */
const WeekDigestRow: React.FC<WeekDigestRowProps> = ({
  weeks, digests, selected, onSelect, open, onToggle,
}) => {
  const display = describeDigest(selected ? digests[selected] : undefined)

  return (
    <div className="bg-blush border-t border-black/5" data-testid="week-digest-row">
      <div className="max-w-7xl mx-auto w-full px-4 sm:px-10 lg:px-12">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          className="flex w-full items-center gap-2 py-2.5 text-left"
        >
          <motion.span
            animate={{ rotate: open ? 90 : 0 }}
            transition={BUTTON_SPRING}
            className="text-gray-400"
          >
            <ChevronRight className="h-4 w-4" />
          </motion.span>
          <span className="text-sm font-medium text-gray-700">Week digest</span>
          {selected && (
            <span className="text-sm text-gray-500" data-testid="week-digest-heading">
              {weekRangeLabel(selected, weekEndFor(selected))}
            </span>
          )}
          {selected && <ModeBadge display={display} />}
        </button>

        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: -8 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -8 }}
              transition={SURFACE_SPRING}
              style={{ transformOrigin: 'top center' }}
              className="pb-4"
              data-testid="week-digest-panel"
            >
              {/* Week picker — the month's weeks, so a whole month can be read
                  without going back to the landscape. Paging months does the
                  rest. */}
              <div className="mb-3 flex flex-wrap gap-1.5">
                {weeks.map((monday) => {
                  const active = monday === selected
                  return (
                    <motion.button
                      key={monday}
                      type="button"
                      onClick={() => onSelect(monday)}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      transition={BUTTON_SPRING}
                      data-testid="week-digest-tab"
                      data-week={monday}
                      aria-pressed={active}
                      className={`rounded-full px-2.5 py-1 text-xs transition-colors ${
                        active
                          ? 'bg-gray-800 text-white'
                          : 'bg-white/70 text-gray-600 hover:bg-white'
                      }`}
                    >
                      {weekRangeLabel(monday, weekEndFor(monday))}
                    </motion.button>
                  )
                })}
              </div>

              {display.detail && (
                <p className="mb-1.5 text-xs text-gray-500" data-testid="week-digest-detail">
                  {display.detail}
                </p>
              )}
              {display.text && (
                <p
                  className="max-w-3xl whitespace-pre-line text-sm leading-relaxed text-gray-700"
                  data-testid="week-digest-text"
                >
                  {display.text}
                </p>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}

const ProjectActivityAreaChart: React.FC<ProjectActivityAreaChartProps> = ({
  className = ''
}) => {
  const [checkins, setCheckins] = useState<Checkin[]>([])
  const [tasks, setTasks] = useState<any[]>([])
  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [hiddenProjects, setHiddenProjects] = useState<Set<number>>(new Set())
  const [monthOffset, setMonthOffset] = useState(0) // 0 = current month, -1 = last month, etc.
  // The month the loaded data belongs to. It trails monthOffset during a
  // fetch, so the previous month's landscape stays up (dimmed) instead of the
  // whole card flashing to a placeholder, then dissolves to the new month.
  const [displayedOffset, setDisplayedOffset] = useState(0)
  const [hasLoaded, setHasLoaded] = useState(false)
  const [width, setWidth] = useState(800)
  const [hover, setHover] = useState<HoverState | null>(null)
  const [showSubtitle, setShowSubtitle] = useState(false)
  const cardRef = useRef<HTMLDivElement>(null)
  // Week digests for the displayed month, keyed by Monday. Only weeks that have
  // actually been built are in here — a gap is a week with no row.
  const [digests, setDigests] = useState<Record<string, WeekDigest>>({})
  const [digestOpen, setDigestOpen] = useState(false)
  const [selectedWeek, setSelectedWeek] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const fetchActivityData = async () => {
      setLoading(true)
      setError(null)

      try {
        const today = new Date()
        const targetDate = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1)
        const endDate = new Date(targetDate.getFullYear(), targetDate.getMonth() + 1, 0) // Last day of month
        const startDate = new Date(targetDate.getFullYear(), targetDate.getMonth(), 1) // First day of month

        const [checkinsResponse, projectsResponse] = await Promise.all([
          getCheckins(
            undefined, // featureId
            undefined, // projectId - get all projects
            1000, // limit
            undefined, // textSearch
            'most_recent', // sortOrder
            toLocalDateStr(startDate),
            toLocalDateStr(endDate)
          ),
          getProjects()
        ])

        if (cancelled) return
        setCheckins(checkinsResponse.checkins)
        setTasks(checkinsResponse.tasks || [])
        setProjects(projectsResponse)
        setDisplayedOffset(monthOffset)
        setHasLoaded(true)
      } catch (err) {
        if (cancelled) return
        console.error('Error fetching activity data:', err)
        setError('Failed to load activity data')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    fetchActivityData()
    return () => { cancelled = true }
  }, [monthOffset])

  // The month's week digests, fetched alongside the landscape. Keyed on the
  // MONDAYS the month touches, not on its first/last day: the endpoint matches a
  // week by its Monday, so a month starting mid-week would otherwise lose the
  // week it opens in. One request covers the 4-6 weeks a month spans.
  //
  // A failure leaves the row empty rather than surfacing anything: the digest is
  // a bonus on this card, and the landscape is what the user came for.
  useEffect(() => {
    const today = new Date()
    const target = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1)
    const first = toLocalDateStr(target)
    const last = toLocalDateStr(new Date(target.getFullYear(), target.getMonth() + 1, 0))

    let cancelled = false
    getWeekDigests(weekStartFor(first), weekStartFor(last))
      .then(res => {
        if (cancelled) return
        setDigests(Object.fromEntries(res.weeks.map(w => [w.period_start, w])))
      })
      .catch(err => {
        if (cancelled) return
        console.error('Error fetching week digests:', err)
        setDigests({})
      })
    return () => { cancelled = true }
  }, [monthOffset])

  useEffect(() => {
    const el = cardRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => setWidth(el.clientWidth || 800))
    ro.observe(el)
    setWidth(el.clientWidth || 800)
    return () => ro.disconnect()
  }, [loading])

  const { dates, series, monthLabel } = useMemo(
    () => buildMonthActivity(checkins, tasks, projects, displayedOffset),
    [checkins, tasks, projects, displayedOffset]
  )

  // The weeks the displayed month touches. Derived from `dates`, so it trails
  // the fetch exactly like the ridges do.
  const weeks = useMemo(() => weeksForMonth(dates), [dates])

  // Re-anchor the selection whenever the month's weeks or their digests change:
  // the most recent week that HAS a digest, else the month's last week (so the
  // row still names a week and can say it has none). A click on the landscape or
  // a week tab overrides this and sticks, since neither changes these inputs.
  useEffect(() => {
    if (!weeks.length) {
      setSelectedWeek(null)
      return
    }
    const built = [...weeks].reverse().find(monday => digests[monday])
    setSelectedWeek(built ?? weeks[weeks.length - 1])
  }, [weeks, digests])

  // The scene tracks the viewer's clock, re-checked each minute. For design
  // tuning, ?mgHour=n pins it to an hour and ?mgHour=demo sweeps a whole day
  // in about 48 seconds.
  const mgHourParam = useMemo(
    () => new URLSearchParams(window.location.search).get('mgHour'),
    []
  )
  const demoMode = mgHourParam === 'demo'
  const hourOverride = useMemo(() => {
    const parsed = mgHourParam !== null ? parseFloat(mgHourParam) : NaN
    return Number.isNaN(parsed) ? null : parsed
  }, [mgHourParam])
  const [now, setNow] = useState(() => new Date())
  const [demoHour, setDemoHour] = useState(5) // dawn is the best opening shot
  useEffect(() => {
    if (demoMode) {
      const start = performance.now()
      let raf = requestAnimationFrame(function tick() {
        setDemoHour((5 + (performance.now() - start) / 2000) % 24) // 2s per hour
        raf = requestAnimationFrame(tick)
      })
      return () => cancelAnimationFrame(raf)
    }
    const id = setInterval(() => setNow(new Date()), 60_000)
    return () => clearInterval(id)
  }, [demoMode])
  const hour = demoMode ? demoHour : hourOverride ?? now.getHours() + now.getMinutes() / 60
  const scheme = schemeForHour(hour)
  const ridgeColors = DAY_RIDGE_COLORS.map((c, i) => lerpHex(c, NIGHT_RIDGE_COLORS[i], scheme.ridgeT))
  const rimColors = DAY_RIM_COLORS.map((c, i) => lerpHex(c, NIGHT_RIM_COLORS[i], scheme.ridgeT))

  // Spread the ramp over however many ridges there are, so the farthest is
  // always the palest — two projects get the two ends, not the two darks.
  // Assigned on the full ranked list, so legend-toggling doesn't repaint.
  const colorFor = (rank: number) =>
    series.length > 1 ? Math.round(rank * (MAX_PROJECTS - 1) / (series.length - 1)) : 0

  // Geometry. Visible ridges keep the color of their slot in the full ranked
  // list, so toggling one in the legend doesn't repaint the others.
  const scene = useMemo(() => {
    const W = width
    const H = Math.min(320, Math.max(220, W * 0.32))
    const GROUND = 16
    const groundY = H - GROUND
    const nDays = dates.length

    const visible = series
      .map((s, rank) => ({ ...s, colorIdx: colorFor(rank) }))
      .filter(s => !hiddenProjects.has(s.project.id))

    const cum: number[][] = []
    visible.forEach((s, k) => {
      const sm = smoothValues(s.values)
      cum[k] = sm.map((v, d) => v + (k ? cum[k - 1][d] : 0))
    })
    const maxV = Math.max(5, ...(cum.length ? cum[cum.length - 1] : [0]))
    const scale = (groundY - H * 0.3) / maxV // leave 30% of height as sky headroom
    const x = (d: number) => (nDays > 1 ? d / (nDays - 1) * W : W / 2)
    const y = (v: number) => groundY - v * scale

    const ridges = visible.map((s, k) => {
      const pts: [number, number][] = s.values.map((_, d) => [x(d), y(cum[k][d])])
      const top = monotoneTopEdge(pts)
      return { ...s, top, area: `${top} L ${W} ${H} L 0 ${H} Z`, depth: k }
    })

    return { W, H, GROUND, groundY, ridges, x, visible }
  }, [width, dates, series, hiddenProjects])

  const handleMouseMove = (e: React.MouseEvent) => {
    const el = cardRef.current
    if (!el || !dates.length) return
    const rect = el.getBoundingClientRect()
    if (!rect.width) return
    const relX = (e.clientX - rect.left) / rect.width * scene.W
    const dayIdx = Math.max(0, Math.min(dates.length - 1, Math.round(relX / scene.W * (dates.length - 1))))
    setHover({ dayIdx, x: e.clientX - rect.left, y: e.clientY - rect.top })
  }

  // Clicking the landscape selects the WEEK the pointed-at day belongs to and
  // opens the digest row. `hover` is what identifies the day, and the overlays
  // that clear it (the month nav) are therefore also the ones a click can't
  // reach — nothing to select, so nothing happens.
  const handleClick = () => {
    if (!hover || !dates.length) return
    setSelectedWeek(weekStartFor(dates[hover.dayIdx]))
    setDigestOpen(true)
  }

  // Toggle project visibility
  const toggleProjectVisibility = (projectId: number) => {
    setHiddenProjects(prev => {
      const newSet = new Set(prev)
      if (newSet.has(projectId)) {
        newSet.delete(projectId)
      } else {
        newSet.add(projectId)
      }
      return newSet
    })
  }

  // Navigation functions
  const goToPreviousMonth = () => {
    setMonthOffset(monthOffset - 1)
  }

  const goToNextMonth = () => {
    if (monthOffset < 0) {
      setMonthOffset(monthOffset + 1)
    }
  }

  const goToCurrentMonth = () => {
    setMonthOffset(0)
  }

  const isCurrentMonth = monthOffset === 0

  if (error) {
    return (
      <div className={`p-4 ${className}`}>
        <div>
          <h2 className="text-2xl font-semibold text-gray-800">Activity</h2>
          <p className="text-sm text-gray-500 mb-4">Checkins and tasks</p>
        </div>
        <div className="text-rose-500 text-sm">{error}</div>
      </div>
    )
  }

  const { W, H, GROUND, groundY, ridges } = scene
  const tickStep = W < 560 ? 10 : 5
  // The sun (or moon, after sunset) rides an arc: on the horizon at rise and
  // set, highest at the middle of its span.
  const cel = celestialForHour(hour)
  const sunX = W * (0.08 + 0.84 * cel.progress)
  const sunY = groundY - Math.sin(Math.PI * cel.progress) * (groundY - H * 0.16)
  const hoveredDate = hover ? dates[hover.dayIdx] : null
  const hoveredRows = hover
    ? scene.visible
        .map(s => ({ ...s, count: s.values[hover.dayIdx] }))
        .filter(s => s.count > 0)
    : []


  return (
    <div className={className}>
      <div
        ref={cardRef}
        data-testid="activity-landscape"
        className="relative overflow-hidden shadow-lg cursor-pointer"
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setHover(null)}
        onClick={handleClick}
      >
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} className="block" role="img" aria-label={`Daily activity per project for ${monthLabel}`}>
          <defs>
            <linearGradient id="arg-sky" x1="0" y1="0" x2="0" y2="1">
              {scheme.sky.map((c, i) => (
                <stop key={i} offset={[0, 0.35, 0.6, 0.8, 1][i]} stopColor={c} />
              ))}
            </linearGradient>
            <radialGradient id="arg-sun" cx="0.5" cy="0.5" r="0.5">
              <stop offset="0" stopColor={scheme.sun} stopOpacity=".95" />
              <stop offset=".25" stopColor={scheme.sky[4]} stopOpacity=".55" />
              <stop offset="1" stopColor={scheme.sky[4]} stopOpacity="0" />
            </radialGradient>
            <linearGradient id="arg-fog" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor={scheme.sky[4]} stopOpacity="0" />
              <stop offset="1" stopColor={scheme.sky[4]} stopOpacity=".35" />
            </linearGradient>
            <filter id="arg-grain">
              <feTurbulence type="fractalNoise" baseFrequency="0.8" numOctaves="2" stitchTiles="stitch" />
              <feColorMatrix type="matrix" values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0.6 0.6 0.6 0 0" />
              <feComposite operator="in" in2="SourceGraphic" />
            </filter>
          </defs>

          {/* sky, stars, sun/moon */}
          <rect width={W} height={H} fill="url(#arg-sky)" />
          {scheme.stars > 0 && STARS.map((star, i) => (
            <circle key={i} cx={star.x * W} cy={star.y * H} r={star.r} fill="#F5F0FF"
              opacity={scheme.stars * (0.35 + (i % 5) * 0.15)} />
          ))}
          <circle cx={sunX} cy={sunY} r={H * (cel.body === 'sun' ? 0.55 : 0.35)} fill="url(#arg-sun)" />
          <circle cx={sunX} cy={sunY} r={H * (cel.body === 'sun' ? 0.045 : 0.04)} fill={scheme.sun} opacity=".9" />

          {/* Terrain dissolves between months and dims while the next one
              loads; the sky, sun and stars stay put. */}
          <AnimatePresence initial={false}>
            <motion.g
              key={`${displayedOffset}-${hasLoaded}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: loading ? 0.6 : 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
            >
              {/* ridges, back (least active, palest) → front */}
              {[...ridges].reverse().map(r => (
                <g key={r.project.id}>
                  <path d={r.area} fill={ridgeColors[r.colorIdx]} />
                  <path d={r.top} fill="none" stroke={rimColors[r.colorIdx]} strokeWidth="1.4" opacity={0.5 + r.depth * 0.12} />
                  {r.depth > 0 && (
                    <path d={r.area} fill="url(#arg-fog)" opacity={0.1 + r.depth * 0.05} />
                  )}
                </g>
              ))}

              {/* ground strip: the y = 0 baseline */}
              <rect x="0" y={groundY} width={W} height={GROUND} fill={scheme.ground} />

              {/* date ticks. The landscape is full-bleed, so the first and
                  last days sit on the viewport's edges. Their labels are
                  pulled in to the overlays' 16px inset (the title's left-4,
                  the month nav's right-4): drawn from the edge itself, the
                  first glyph touches the screen border and reads as cut. */}
              {dates.map((dateStr, d) => {
                if (d % tickStep !== 0) return null
                const tx = scene.x(d)
                const anchor = d === 0 ? 'start' : (tx > W - 24 ? 'end' : 'middle')
                const lx = anchor === 'start'
                  ? Math.max(tx, TICK_EDGE_INSET)
                  : anchor === 'end' ? Math.min(tx, W - TICK_EDGE_INSET) : tx
                return (
                  <text key={dateStr} x={lx} y={H - 5} fontSize="9.5" fill="rgba(255,240,220,.55)" textAnchor={anchor}>
                    {toShortLabel(dateStr)}
                  </text>
                )
              })}
            </motion.g>
          </AnimatePresence>

          {/* film grain */}
          <rect width={W} height={H} filter="url(#arg-grain)" opacity=".05" />

          {hover && (
            <line
              x1={scene.x(hover.dayIdx)}
              x2={scene.x(hover.dayIdx)}
              y1={H * 0.18}
              y2={H}
              stroke="rgba(255,240,220,.5)"
              strokeWidth="1"
              strokeDasharray="2 3"
            />
          )}
        </svg>

        {/* title overlay; the title toggles its explainer */}
        <div className="absolute top-3.5 left-4 pointer-events-none text-[#fff4e6]/90 [text-shadow:0_1px_8px_rgba(40,25,60,.5)]">
          <h2 className="text-xl font-semibold">
            <button
              type="button"
              onClick={e => { e.stopPropagation(); setShowSubtitle(v => !v) }}
              className="pointer-events-auto"
              aria-expanded={showSubtitle}
            >
              Activity
            </button>
          </h2>
          <AnimatePresence>
            {showSubtitle && (
              <motion.p
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 0.75, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                className="text-xs"
              >
                Checkins and tasks
              </motion.p>
            )}
          </AnimatePresence>
        </div>

        {/* month navigation overlay */}
        <div
          className="absolute top-4 right-4 flex items-center gap-2 text-[#fff4e6]/90 [text-shadow:0_1px_6px_rgba(40,25,60,.5)]"
          onMouseMove={e => { e.stopPropagation(); setHover(null) }}
          onClick={e => e.stopPropagation()}
        >
          <button
            onClick={goToPreviousMonth}
            className="p-1.5 rounded-md bg-[#fff4e6]/10 hover:bg-[#fff4e6]/25 transition-colors"
            title="Previous month"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="text-sm font-medium min-w-[110px] text-center">
            {monthLabelFor(monthOffset)}
          </div>
          <button
            onClick={goToNextMonth}
            className="p-1.5 rounded-md bg-[#fff4e6]/10 hover:bg-[#fff4e6]/25 transition-colors disabled:opacity-40"
            title="Next month"
            disabled={isCurrentMonth}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          <button
            onClick={goToCurrentMonth}
            className="p-1.5 rounded-md bg-[#fff4e6]/10 hover:bg-[#fff4e6]/25 transition-colors disabled:opacity-40"
            title="Go to current month"
            disabled={isCurrentMonth}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5l7 7-7 7M12 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* quiet month */}
        {hasLoaded && series.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <p className="text-sm text-[#fff4e6]/70 [text-shadow:0_1px_6px_rgba(40,25,60,.5)]">
              No activity this month
            </p>
          </div>
        )}

        {/* tooltip */}
        <AnimatePresence>
          {hover && hoveredDate && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: -8 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -8 }}
              transition={{ type: 'spring', stiffness: 500, damping: 30 }}
              className="absolute z-10 pointer-events-none bg-[#1c142c]/90 text-[#f5ead9] rounded-xl px-3 py-2 text-xs leading-relaxed shadow-lg backdrop-blur-sm"
              style={{
                left: Math.min(Math.max(hover.x + 14, 4), Math.max(width - 180, 4)),
                // Keep the whole box inside the card — overflow-hidden would
                // otherwise clip rows when hovering near the bottom.
                top: Math.min(hover.y + 14, H - (40 + Math.max(hoveredRows.length, 1) * 18) - 6),
                transformOrigin: 'top center',
              }}
            >
              <div className="font-semibold text-[#ffd9a4] mb-0.5">{toShortLabel(hoveredDate)}</div>
              {hoveredRows.length === 0 && <div className="opacity-70">A quiet day</div>}
              {hoveredRows.map(row => (
                <div key={row.project.id} className="flex items-center gap-1.5">
                  <span
                    className="w-2 h-2 rounded-sm inline-block outline outline-1 outline-white/30"
                    style={{ backgroundColor: ridgeColors[row.colorIdx] }}
                  />
                  {row.project.emoji} {row.project.title}: {row.count} {row.count !== 1 ? 'actions' : 'action'}
                </div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Legend. It rides a full-bleed band of its own, so the strip between
          the landscape and the cards reads as part of the chart rather than as
          page gutter. The pink is light on purpose — deep enough to separate
          the band from the page's gray, light enough not to read as a bar.
          The swatches take a hairline, since the pale back-ridge colors have
          no edge of their own against a warm ground.

          The band is full-bleed but the legend inside it belongs to the page's
          content column: these paddings reproduce the dashboard's container
          (max-w-7xl + px-6/px-8, plus the cards' own mx-4) so the first swatch
          sits on the left edge of the cards below. */}
      {series.length > 0 && (
        <div className="bg-blush">
          <div className="max-w-7xl mx-auto w-full px-4 sm:px-10 lg:px-12 py-3">
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              {series.map((s, idx) => {
                const isHidden = hiddenProjects.has(s.project.id)
                return (
                  <div
                    key={s.project.id}
                    className="flex items-center gap-1.5 cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => toggleProjectVisibility(s.project.id)}
                    style={{ opacity: isHidden ? 0.4 : 1 }}
                  >
                    <div
                      className="w-3 h-3 rounded-sm ring-1 ring-black/10"
                      style={{ backgroundColor: ridgeColors[colorFor(idx)] }}
                    />
                    <span className="text-lg">{s.project.emoji}</span>
                    <span className="text-sm text-gray-600">{s.project.title}</span>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {/* The week digests, under the legend — the last band of the hero. Always
          present (collapsed), because "this week has no digest" is itself the
          answer a reviewer needs, and a row that came and went with the data
          would be harder to find than to read. */}
      <WeekDigestRow
        weeks={weeks}
        digests={digests}
        selected={selectedWeek}
        onSelect={setSelectedWeek}
        open={digestOpen}
        onToggle={() => setDigestOpen(v => !v)}
      />
    </div>
  )
}

export default ProjectActivityAreaChart
